import {
  type CSSProperties,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useGetLoggedInUser } from '@revelio/auth';

import { useKibanaLogger } from '../../../../../shared/hooks/kibana-logger/kibana-logger';
import { useGaChartPreviewMeta } from '../../../../global-ai-assistant/ga-chart-preview-meta-context';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import { usePlotMetric } from '../../../hooks/usePlotMetric';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { getChartDataSourceAccessErrorMessage } from '../../DataProvider/utils/chart-data-source-access';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PrimaryFilterConfig } from '../../PageConfigProvider/page-reducer';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type { PositionedItem } from '../../PageConfigProvider/types/item';
import { isSingleEntityChart } from '../../PageState/utils/is-single-entity-chart';
import { useRegisterSharedChartData } from '../../SharedReport/SharedReportDataContext';
import { ChartError } from '../ChartError';
import { ChartSwitchers } from '../ChartHeader';
import { ChartNotSupported } from '../ChartNotSupported';
import { ChartProvider } from '../ChartProvider';
import { ChartMenuDataProvider } from '../chart-menu/ChartMenuContext';
import { ExpandModalProvider } from '../chart-menu/ExpandModal';
import { CustomTableChart } from '../Table/custom-table-chart';
import { TableChart } from '../Table/TableChart';
import { useProcessContentSync } from '../TextChart/utils/use-process-content';
import { getSegmentForChartTitle } from '../utils/get-segment-for-chart-title';
import styles from './Chart.module.css';
import { canDrawResolvedData } from './can-draw-resolved-data';
import { classifyChartDataRoute } from './classify-chart-data-route';
import { DataWrappedChart } from './DataWrappedChart';
import { SpecialChart } from './SpecialChart';

const isSpecialTablePage = (revelioPageId: RevelioPageId): boolean => {
  return [
    RevelioPageId.TALENT_DISCOVERY,
    RevelioPageId.POSTING_DISCOVERY,
    RevelioPageId.COMPANY_SCREENER,
    RevelioPageId.GEOGRAPHY_SCREENER,
    RevelioPageId.ROLE_SCREENER,
  ].includes(revelioPageId);
};

export type ChartProps = {
  editMode?: boolean;
  item: PositionedItem;
  data?: ResolvedData;
  headerSize?: 'sm' | 'md';
  primaryFilter?: PrimaryFilterConfig;
  variant?: 'default' | 'report';
  onClick?: MouseEventHandler<HTMLDivElement>;
};

export const Chart = (props: ChartProps) => {
  return (
    <ChartProvider
      item={props.item}
      variant={props.variant}
      onEditClick={props.onClick}
    >
      <ChartMenuDataProvider>
        <ChartCardErrorBoundary>
          <ExpandModalProvider
            controls={
              <ChartSwitchers
                primaryFilterId={props.primaryFilter?.id}
                isSingleEntityChart={isSingleEntityChart(props.item)}
              />
            }
          >
            <ChartRootSelector {...props} />
          </ExpandModalProvider>
        </ChartCardErrorBoundary>
      </ChartMenuDataProvider>
    </ChartProvider>
  );
};

const ChartCardErrorFallback = () => (
  <ChartCard>
    <ChartError message="An error occurred while loading the chart. Please try refreshing the page." />
  </ChartCard>
);

const ChartCardErrorBoundary = ({ children }: { children: ReactNode }) => {
  const { kibanaLogger } = useKibanaLogger();

  const handleError = useCallback(
    (error: Error) => {
      console.error('ChartErrorBoundary', error);

      if (kibanaLogger === undefined) return;

      const message = error.stack
        ? `ChartErrorBoundary: ${error.message}\n${error.stack}`
        : `ChartErrorBoundary: ${error.message}`;
      kibanaLogger.error(message, {
        context: 'ChartErrorBoundary',
      });
    },
    [kibanaLogger]
  );

  return (
    <ErrorBoundary
      FallbackComponent={ChartCardErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export const ChartCard = ({
  onClick,
  children,
  isRelative,
  variant,
  gaPreviewMinHeight,
}: PropsWithChildren<
  Pick<ChartProps, 'onClick'> & {
    isRelative?: boolean;
    variant?: 'report-table' | 'screener';
    gaPreviewMinHeight?: string;
  }
>) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;

  return (
    <div
      ref={cardRef}
      role="group"
      // Chrome Translate re-parents rendered text nodes into <font> wrappers:
      // React commits that later delete those text fibers throw NotFoundError
      // (removeChild) and crash the chart into its error card, while in-place
      // text updates land on the detached originals and display stale values.
      // Charts render data (numbers, entity names) — keep translation off.
      translate="no"
      data-testid="card"
      data-ga-preview={isGlobalAiChartPreview || undefined}
      data-relative={isRelative || undefined}
      data-card-variant={variant}
      style={
        gaPreviewMinHeight
          ? ({
              '--chart-card-min-height': gaPreviewMinHeight,
            } as CSSProperties)
          : undefined
      }
      onClick={onClick}
      className={styles.card}
    >
      {children}
    </div>
  );
};

const ChartRootSelector = ({
  editMode,
  item,
  headerSize = 'md',
  primaryFilter,
  data,
  onClick,
}: ChartProps) => {
  const chartType = item?.plot?.chartType;

  const { revelioPageId } = useTabContentConfig();
  const title = item?.title ?? '';
  const metric = usePlotMetric();
  const primaryFilterId = primaryFilter?.id;
  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryFilterId);

  const gaChartPreviewMeta = useGaChartPreviewMeta();
  const isGlobalAiChartPreview = gaChartPreviewMeta !== null;
  const { loggedInUser } = useGetLoggedInUser();

  const segment = getSegmentForChartTitle(item);
  const processedTitle = useProcessContentSync({
    content: title,
    currentMetric: metric,
    segment,
    diagnosticsMode: false,
  });
  useRegisterSharedChartData(item.id, { title: processedTitle });

  const accessErrorMessage =
    !revelioPageId || isGlobalAiChartPreview
      ? getChartDataSourceAccessErrorMessage({
          metric,
          chartType,
          secondaryDimension: item?.plot?.secondaryDimension,
          userTabs: loggedInUser.tabs ?? [],
        })
      : undefined;

  if (accessErrorMessage) {
    return (
      <>
        {gaChartPreviewMeta ? (
          <GlobalAiChartPreviewDataReporter
            itemId={item.id}
            branch="main"
            status={DataProviderQueryStatus.Error}
            error={new Error(accessErrorMessage)}
            data={undefined}
            chartType={chartType ?? ''}
            metric={metric}
          />
        ) : null}
        <ChartCard onClick={onClick}>
          <ChartError message={accessErrorMessage} />
        </ChartCard>
      </>
    );
  }

  /** Company Overview Check primary selection is company
   * if selection is not company (industr) show error card */
  if (
    revelioPageId === RevelioPageId.COMPANY_OVERVIEW &&
    primaryFilterState?.selections?.some(
      (selection) => selection?.levelId !== 'company'
    )
  ) {
    return (
      <ChartCard onClick={onClick}>
        <ChartNotSupported>
          Please select a company to see its overview.
        </ChartNotSupported>
      </ChartCard>
    );
  }

  if (!chartType) {
    return (
      <ChartCard onClick={onClick}>
        <ChartNotSupported />
      </ChartCard>
    );
  }

  // Shared with the assistant's headless digest router — both trees must
  // branch off the same classifier so the headless mirror can't drift.
  const route = classifyChartDataRoute(item);
  const resolvedData =
    data && canDrawResolvedData(item, metric) ? data : undefined;

  if (route === 'table') {
    // Which table component renders is a page-context decision (discovery/
    // screener pages have a bespoke table), not part of the shared data-route.
    if (isSpecialTablePage(revelioPageId as RevelioPageId)) {
      return (
        <TableChart
          item={item}
          primaryFilter={primaryFilter}
          title={processedTitle}
          onClick={onClick}
        />
      );
    }
    return (
      <CustomTableChart
        primaryDimension={primaryFilterId}
        data={resolvedData}
        onClick={onClick}
      />
    );
  }

  if (route === 'special') {
    return (
      <SpecialChart
        editMode={editMode}
        item={item}
        data={resolvedData}
        headerSize={headerSize}
        primaryFilter={primaryFilter}
        chartTitle={processedTitle}
        metric={metric}
        onClick={onClick}
      />
    );
  }

  // snowflake / multi-metric / main all render through DataWrappedChart,
  // which consults the same classifier to pick its data provider.
  return (
    <DataWrappedChart
      item={item}
      editMode={editMode}
      chartType={chartType}
      headerSize={headerSize}
      chartTitle={processedTitle}
      primaryFilterId={primaryFilterId}
      secondaryDimension={item?.plot?.secondaryDimension}
      metric={metric}
      pageId={revelioPageId}
      data={resolvedData}
      onClick={onClick}
    />
  );
};
